import Controller from '@ember/controller';
import { action } from '@ember/object';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';
import CustomFormsService from 'cio/services/custom-forms';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import IntegrationsFormsController from 'cio/pods/integrations/forms/controller';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomForm from 'cio/models/custom-form';
import CampaignModel from 'cio/models/campaign';
import AnalyticsService from 'cio/services/analytics';
import { FormIntegrationDetailPanes } from 'cio/components/forms/custom/integration-details';
import { all, task, timeout } from 'ember-concurrency';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import config from 'cio/config/environment';

type StatusFilterQueryParam = 'all' | 'disconnected' | null;

export default class IntegrationsFormsGeneralIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;

  @controller('integrations/forms') baseController!: IntegrationsFormsController;

  readonly FormIntegrationDetailPanes = FormIntegrationDetailPanes;

  queryParams = ['status'];
  status: StatusFilterQueryParam = null;

  formCampaigns!: CampaignModel[];

  statusFilterSelectItems = Object.freeze([
    { value: 'all', label: 'All' },
    { value: 'connected', label: 'Connected' },
    { value: 'disconnected', label: 'Disconnected' },
  ]);

  @computed('status')
  get statusForSelect() {
    return this.status ?? 'connected';
  }

  @computed(
    'status',
    'facebookLeadAds.forms.@each.{isConnected,formSegment}',
    'customForms.forms.@each.isConnectedOrDraft',
  )
  get sortedDisplayedForms() {
    let forms: (FacebookLeadAdsForm | CustomForm)[];

    // show only forms with segments: so either connected now or at one stage before
    if (this.status === 'all') {
      forms = [...this.facebookLeadAds.forms.filterBy('formSegment'), ...this.customForms.forms];
    } else if (this.status === 'disconnected') {
      forms = [
        ...this.facebookLeadAds.forms.filter((f) => f.isDisconnected && f.formSegment),
        ...this.customForms.disconnectedForms,
      ];
    } else {
      // default value (null): display only connected forms
      forms = [...this.facebookLeadAds.forms.filterBy('isConnected'), ...this.customForms.connectedOrDraftForms];
    }

    // - primary sort by form name
    // - secondary sort by form ID
    return forms
      .sort((a, b) => {
        let textA = (a instanceof FacebookLeadAdsForm ? a.facebookFormId : a.formId).toLowerCase();
        let textB = (b instanceof FacebookLeadAdsForm ? b.facebookFormId : b.formId).toLowerCase();
        return textA.localeCompare(textB, undefined, { sensitivity: 'accent' });
      })
      .sort((a, b) => {
        let textA = (a instanceof FacebookLeadAdsForm ? a.formNameForDisplay : a.name).toLowerCase();
        let textB = (b instanceof FacebookLeadAdsForm ? b.formNameForDisplay : b.name).toLowerCase();
        return textA.localeCompare(textB, undefined, { sensitivity: 'accent' });
      });
  }

  @action setStatus(status: StatusFilterQueryParam | 'connected') {
    this.set('status', status === 'connected' ? null : status);

    this.trackListViewedOrFiltered();
  }

  trackListViewedOrFiltered() {
    let filteredTo = this.statusFilterSelectItems.find((it) => it.value === this.statusForSelect)?.label;

    this.analytics.track('forms', 'list_viewed_or_filtered', {
      filteredTo,
    });
  }

  initializeServicesTask = task({ restartable: true }, async () => {
    await timeout(config.APP.feedbackDelay);
    try {
      await all([
        // load segments and campaigns matched to forms
        // used when displaying them on both the list and detail views
        this.store.query('segment', {}),
        this.store.query('campaign', { type: 'form', sort: 'name', asc: 'true' }),
        this.facebookLeadAds.initialize(),
        this.customForms.initialize(),
      ]);
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'integrations/forms/general/index': IntegrationsFormsGeneralIndexController;
  }
}
